import { useCallback, useEffect, useState } from 'react';

/** How far ahead of the viewport an element counts as "near" — rows scrolling in find their image ready. */
export const NEAR_VIEWPORT_MARGIN = '200px';

type Listener = (near: boolean) => void;

/**
 * One IntersectionObserver per root margin, shared by every element that asks — a page of 100
 * thumbs registers 100 targets on one observer instead of creating 100 observers.
 */
const observers = new Map<
  string,
  { observer: IntersectionObserver; listeners: Map<Element, Listener> }
>();

function sharedObserver(rootMargin: string) {
  let entry = observers.get(rootMargin);
  if (!entry) {
    const listeners = new Map<Element, Listener>();
    const observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          if (record.isIntersecting) listeners.get(record.target)?.(true);
        }
      },
      { rootMargin },
    );
    entry = { observer, listeners };
    observers.set(rootMargin, entry);
  }
  return entry;
}

/**
 * True once the element has come within `rootMargin` of the viewport, and stays true (once near,
 * always near). Returns a callback ref for the element. Lets images and per-row reads wait until
 * the row is about to be seen; without IntersectionObserver (jsdom, old browsers) it is true at once.
 */
export function useNearViewport(rootMargin: string = NEAR_VIEWPORT_MARGIN) {
  const [element, setElement] = useState<Element | null>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (!element || near) return;
    const { observer, listeners } = sharedObserver(rootMargin);
    listeners.set(element, setNear);
    observer.observe(element);
    return () => {
      observer.unobserve(element);
      listeners.delete(element);
    };
  }, [element, near, rootMargin]);

  const ref = useCallback((el: Element | null) => {
    setElement(el);
  }, []);

  return [ref, near] as const;
}
